import { escapeHtml } from './security';
// Report documents (PLAN §10.4: "PDF via Browser Rendering (HTML report template → PDF) and DOCX via `docx` lib,
// embedding graph/timeline snapshots. Watermarked with user + timestamp + export ID"; task 36). One model feeds both
// renderers: the HTML page here (printed to PDF) and the DOCX in the app Worker. Every page of either file carries the
// watermark: a faint diagonal layer across the page plus the footer line, so a cropped or photographed page still
// names who exported it, when, and the export id an admin can look up.

import {
  AGGREGATE_RATES,
  type AggregateGroupBy,
  type AggregateMeasure,
  type AggregateRate,
  type AggregateRequest,
  type ChartType,
} from './charts';
import { gray } from './design/tokens';
import { DOSSIER_FIGURE_KINDS, type DossierFigureKind } from './dossier';
import { BEFORE_AFTER_MAX_PAIRS, type BeforeAfterPair } from './figures/before-after';
import {
  isSchematicLinkKind,
  SCHEMATIC_LABEL_CHARS,
  SCHEMATIC_MAX_COLUMNS,
  SCHEMATIC_MAX_LINKS,
  SCHEMATIC_MAX_NODES,
  type SchematicLink,
  type SchematicNode,
} from './figures/schematic';
import { STATUS_TIMELINE_LABEL_CHARS, STATUS_TIMELINE_MAX_EVENTS, type StatusTimelineEvent } from './figures/status-timeline';
import { TILE_CAPTION_CHARS, TILE_VALUE_CHARS, TILES_MAX, type TileSpec } from './figures/tiles';
import { textDirection } from './graph';
import type { ExportFormat } from './profile-export';
import { isReliabilityStatus } from './reliability';
import type { ReportPlan } from './report-plan';
import { type ParseResult, parseReportQuery, REPORT_LIMITS } from './reports';
import { isKey } from './schema/types';
import { SNAPSHOT_LABELS, type Snapshot, type SnapshotKind } from './snapshots';

/** Rows a PDF/DOCX report carries (the CSV/XLSX export goes further). */
export const DOCUMENT_ROWS = 1000;

export const REPORT_DOCUMENT_LABELS: Record<ExportFormat, string> = { pdf: 'PDF', docx: 'Word (DOCX)' };

export interface ReportDocument {
  title: string;
  description: string | null;
  /** The question in words. */
  question: string;
  headers: string[];
  rows: string[][];
  /** All matches (rows may stop at DOCUMENT_ROWS). */
  total: number;
  snapshots: { kind: SnapshotKind; caption: string; width: number; height: number }[];
  /** What the report leaves out, said once. */
  withheld: string;
  exportId: string;
  exportedBy: string;
  exportedAt: number;
  watermark: string;
}

export function buildReportDocument(input: {
  name: string;
  description?: string | null;
  question: string;
  table: { headers: string[]; rows: (string | number | null | undefined)[][] };
  total: number;
  snapshots: readonly Pick<Snapshot, 'kind' | 'caption' | 'width' | 'height'>[];
  exportId: string;
  exportedBy: string;
  exportedAt: number;
  watermark: string;
}): ReportDocument {
  return {
    title: input.name,
    description: input.description?.trim() || null,
    question: input.question,
    headers: input.table.headers,
    rows: input.table.rows.slice(0, DOCUMENT_ROWS).map(r => r.map(c => (c === null || c === undefined ? '' : String(c)))),
    total: input.total,
    snapshots: input.snapshots.map(s => ({
      kind: s.kind,
      caption: s.caption || SNAPSHOT_LABELS[s.kind],
      width: s.width,
      height: s.height,
    })),
    withheld: 'Off-record claims are left out of reports and their exports.',
    exportId: input.exportId,
    exportedBy: input.exportedBy,
    exportedAt: input.exportedAt,
    watermark: input.watermark,
  };
}

/** "Showing 12 of 12 matches" / "Showing the first 1,000 of 4,210 matches". */
export function rowsLine(doc: Pick<ReportDocument, 'rows' | 'total'>): string {
  const n = doc.rows.length;
  const fmt = (v: number) => v.toLocaleString('en-GB');
  if (doc.total > n) return `Showing the first ${fmt(n)} of ${fmt(doc.total)} matches.`;
  return `${fmt(doc.total)} ${doc.total === 1 ? 'match' : 'matches'}.`;
}

export const figureCaption = (doc: ReportDocument, i: number) => `Figure ${i + 1}. ${doc.snapshots[i]?.caption ?? ''}`;

// ─── Watermark layer (shared by every HTML export) ──────────────────────────

const esc = escapeHtml;

/**
 * CSS for the watermark layer: fixed-position, so Chromium repeats it on every printed page; faint, diagonal and
 * behind nothing (it never hides data), in the neutral grey tokens.
 */
export const WATERMARK_CSS = `
.hp-watermark { position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; }
.hp-watermark div { position: absolute; left: -20%; width: 140%; transform: rotate(-28deg); text-align: center; white-space: nowrap;
  font-size: 13pt; font-weight: 600; letter-spacing: .04em; color: ${gray[400]}; opacity: .28; }
.hp-content { position: relative; z-index: 1; }
`;

/** The watermark text tiled diagonally across the page (four lines). */
export const watermarkLayer = (watermark: string) =>
  `<div class="hp-watermark" aria-hidden="true" data-watermark="${esc(watermark)}">${[12, 36, 60, 84]
    .map(top => `<div style="top:${top}%">${esc(watermark)}</div>`)
    .join('')}</div>`;

// ─── HTML (the page Browser Rendering prints to PDF) ─────────────────────────

const data = (s: string, tag = 'span') => (s ? `<${tag} dir="${textDirection(s)}">${esc(s)}</${tag}>` : `<${tag}></${tag}>`);

/** The report as a standalone, script-free A4 page; `images` are the snapshots' PNG bytes as base64, in order. */
export function renderReportHtml(doc: ReportDocument, images: readonly string[]): string {
  const css = `
@page { size: A4; margin: 18mm 14mm 20mm; }
* { box-sizing: border-box; }
body { font-family: "Noto Sans", "Noto Naskh Arabic", "Geeza Pro", Arial, sans-serif; font-size: 9pt; line-height: 1.4; color: ${gray[950]}; margin: 0; }
h1 { font-size: 16pt; margin: 0 0 2pt; }
h2 { font-size: 11pt; border-bottom: 1.5px solid ${gray[900]}; padding-bottom: 2pt; margin: 14pt 0 5pt; break-after: avoid; }
.description { margin: 2pt 0 0; white-space: pre-wrap; }
.meta { color: ${gray[600]}; font-size: 8pt; margin: 4pt 0 10pt; }
.question { border: 1px solid ${gray[400]}; padding: 4pt 6pt; margin: 0; }
figure { margin: 8pt 0; break-inside: avoid; }
figure img { display: block; max-width: 100%; max-height: 190mm; border: 1px solid ${gray[300]}; }
figcaption { color: ${gray[700]}; font-size: 8pt; margin-top: 3pt; }
.count { color: ${gray[700]}; margin: 0 0 4pt; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid ${gray[300]}; padding: 2.5pt 4pt; vertical-align: top; text-align: start; overflow-wrap: anywhere; }
thead { display: table-header-group; }
thead th { background: ${gray[100]}; font-weight: 600; }
tr { break-inside: avoid; }
.policy { margin-top: 12pt; border-top: 1px solid ${gray[400]}; padding-top: 4pt; color: ${gray[700]}; font-size: 8pt; }
.policy p { margin: 2pt 0; }
${WATERMARK_CSS}`;
  const figures = doc.snapshots
    .map(
      (s, i) =>
        `<figure><img alt="${esc(s.caption)}" src="data:image/png;base64,${images[i] ?? ''}" width="${s.width}" height="${s.height}" style="width:100%;height:auto"><figcaption>${esc(
          figureCaption(doc, i),
        )}</figcaption></figure>`,
    )
    .join('');
  const table = doc.rows.length
    ? `<table><thead><tr>${doc.headers.map(h => `<th scope="col">${esc(h)}</th>`).join('')}</tr></thead><tbody>${doc.rows
        .map(r => `<tr>${r.map(c => `<td>${data(c)}</td>`).join('')}</tr>`)
        .join('')}</tbody></table>`
    : '<p class="count">Nothing matches this question.</p>';
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(doc.title)}</title><style>${css}</style></head>
<body>
${watermarkLayer(doc.watermark)}
<main class="hp-content">
<h1 dir="${textDirection(doc.title)}">${esc(doc.title)}</h1>
${doc.description ? `<p class="description" dir="${textDirection(doc.description)}">${esc(doc.description)}</p>` : ''}
<p class="meta">Report · exported from live data · ${esc(doc.watermark)}</p>
<h2>Question</h2>
<p class="question">${data(doc.question)}</p>
${figures ? `<h2>${doc.snapshots.length === 1 ? 'Figure' : 'Figures'}</h2>${figures}` : ''}
<h2>Results</h2>
<p class="count">${esc(rowsLine(doc))}</p>
${table}
<div class="policy"><p>${esc(doc.withheld)}</p><p>${esc(doc.watermark)}</p></div>
</main>
</body></html>`;
}

// ─── The report as a document (Report v2) ────────────────────────────────────
// A saved report is read and edited as a document: its blocks (title, executive summary, one block per template
// section, the sources, the three annexes) in the order of its `layout`, with figures placed in sections. The
// question and the template are the machinery behind it. The plan a report was generated from is in report-plan.ts
// (re-exported here so the package index needs no new line).

export * from './report-plan';

export const REPORT_BLOCK_KINDS = ['title', 'summary', 'section', 'sources', 'annex'] as const;
export type ReportBlockKind = (typeof REPORT_BLOCK_KINDS)[number];
export const REPORT_ANNEX_KEYS = ['corrections', 'bibliography', 'glossary'] as const;
export type ReportAnnexKey = (typeof REPORT_ANNEX_KEYS)[number];

export interface ReportBlockOptions {
  table?: boolean;
  allRows?: boolean;
  centrality?: boolean;
  /** A field key: the section's results table is grouped by this field, one sub-heading per value. */
  groupBy?: string;
  /** Print the money links among the section's rows after its table. */
  flowsTable?: boolean;
}

export interface ReportBlock {
  kind: ReportBlockKind;
  /** section: the template section key; annex: 'corrections' | 'bibliography' | 'glossary'; others: the kind. */
  key: string;
  hidden?: boolean;
  /** section only */
  options?: ReportBlockOptions;
}

export interface ReportLayout {
  v: 1;
  blocks: ReportBlock[];
}

/** A source the analyst added by hand to the report's bibliography (the cited ones are never removed). */
export interface BibliographyEntry {
  id: string;
  text: string;
  url?: string | null;
  addedBy: string;
  addedAt: number;
}

export const FIGURE_CHART_TYPES = ['bar', 'stacked', 'grouped', 'line'] as const;
export type FigureChartType = (typeof FIGURE_CHART_TYPES)[number];

/**
 * What is enough to draw a figure again: the document page renders it from the spec and saves the SVG, and the
 * export draws it again on the server from the same spec (the figure library, `figures/`). Data-backed kinds name
 * entities; the typed kinds (tiles, schematic, before/after, status timeline) carry their own small data, with a
 * `source` line printed under the picture.
 */
export type FigureSpec =
  | { kind: 'chart'; request: AggregateRequest; chart: FigureChartType }
  | { kind: 'graph'; entityIds: string[]; depth?: number }
  | { kind: 'timeline'; entityIds: string[] }
  | { kind: 'sankey'; entityIds: string[]; currency: string }
  | { kind: 'clusters'; entityIds: string[] }
  /** A bar chart of the betweenness of the most central of these entities (network tests). */
  | { kind: 'centrality'; entityIds: string[] }
  /** Per-interview cross-check counts of the allegations about these entities, as stacked bars. */
  | { kind: 'crosscheck'; entityIds: string[] }
  /** Key-figure tiles: the numbers as typed, each with its caption. */
  | { kind: 'tiles'; tiles: TileSpec[]; source?: string | null }
  /** Boxes in columns joined by typed links. */
  | { kind: 'schematic'; nodes: SchematicNode[]; links: SchematicLink[]; source?: string | null }
  /** Events in date order, each with its reliability status. */
  | { kind: 'status_timeline'; events: StatusTimelineEvent[]; source?: string | null }
  /** Roll-call votes of these persons (rows) on these vote events (columns; all their votes when empty), from the vote claims. */
  | { kind: 'votes'; entityIds: string[]; eventIds?: string[] }
  /** Two values per row and the ratio between them. */
  | {
      kind: 'before_after';
      pairs: BeforeAfterPair[];
      beforeLabel: string;
      afterLabel: string;
      currency?: string | null;
      source?: string | null;
    };

/** A figure's chart type ↔ the chart renderer's ('stacked' is 'stacked_bar' there, 'grouped' is 'grouped_bar'). */
export const toChartType = (c: FigureChartType): ChartType => (c === 'stacked' ? 'stacked_bar' : c === 'grouped' ? 'grouped_bar' : c);
export const toFigureChart = (t: ChartType): FigureChartType => (t === 'stacked_bar' ? 'stacked' : t === 'grouped_bar' ? 'grouped' : t);

/** Characters of a typed figure's source line. */
export const FIGURE_SOURCE_CHARS = 300;
/** Bytes of saved figure SVG one report may hold, all figures together (the size budget behind the figure count). */
export const REPORT_FIGURE_BUDGET_BYTES = 8_000_000;

export interface ReportFigure {
  id: string;
  reportId: string;
  /** The template section it belongs to; null = before the first section. */
  sectionKey: string | null;
  position: number;
  kind: DossierFigureKind;
  caption: string;
  spec: FigureSpec;
  /** Print-palette SVG (at most REPORT_FIGURE_SVG_BYTES). */
  svg: string;
  width: number;
  height: number;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Bytes of one figure's SVG. */
export const REPORT_FIGURE_SVG_BYTES = 400_000;
/** Entity ids one figure spec may name. */
export const FIGURE_ENTITY_IDS_MAX = 500;

/** The fields GET /reports/:id adds to a `Report` for the document view. */
export interface ReportDocumentMeta {
  brief: string | null;
  /** Never null: the default layout of the report's sections when none was saved. */
  layout: ReportLayout;
  bibliographyExtra: BibliographyEntry[];
  plan: ReportPlan | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Title, summary, each section in order, the sources, then the three annexes; nothing hidden. */
export function defaultLayout(sections: readonly { key: string }[]): ReportLayout {
  return {
    v: 1,
    blocks: [
      { kind: 'title', key: 'title' },
      { kind: 'summary', key: 'summary' },
      ...sections.map(s => ({ kind: 'section' as const, key: s.key })),
      { kind: 'sources', key: 'sources' },
      ...REPORT_ANNEX_KEYS.map(key => ({ kind: 'annex' as const, key })),
    ],
  };
}

const blockId = (b: Pick<ReportBlock, 'kind' | 'key'>) => `${b.kind}:${b.key}`;

function parseOptions(raw: unknown): ReportBlockOptions | undefined {
  if (!isObj(raw)) return undefined;
  const o: ReportBlockOptions = {};
  if (typeof raw.table === 'boolean') o.table = raw.table;
  if (typeof raw.allRows === 'boolean') o.allRows = raw.allRows;
  if (typeof raw.centrality === 'boolean') o.centrality = raw.centrality;
  if (typeof raw.flowsTable === 'boolean') o.flowsTable = raw.flowsTable;
  if (isKey(raw.groupBy)) o.groupBy = raw.groupBy;
  return Object.keys(o).length ? o : undefined;
}

/**
 * A saved layout against the report's current sections: blocks whose key no longer exists (a section removed, an
 * unknown annex) are dropped, blocks the default layout has and this one lacks are appended, duplicates and unknown
 * properties are dropped. Anything that is not a layout at all → null.
 */
export function parseLayout(raw: unknown, sectionKeys: readonly string[]): ReportLayout | null {
  if (!isObj(raw) || raw.v !== 1 || !Array.isArray(raw.blocks)) return null;
  const base = defaultLayout(sectionKeys.map(key => ({ key })));
  const known = new Set(base.blocks.map(blockId));
  const seen = new Set<string>();
  const blocks: ReportBlock[] = [];
  for (const item of raw.blocks) {
    if (!isObj(item) || typeof item.key !== 'string' || typeof item.kind !== 'string') continue;
    const kind = item.kind as ReportBlockKind;
    const id = blockId({ kind, key: item.key });
    if (!known.has(id) || seen.has(id)) continue;
    seen.add(id);
    const block: ReportBlock = { kind, key: item.key };
    if (item.hidden === true) block.hidden = true;
    const options = kind === 'section' ? parseOptions(item.options) : undefined;
    if (options) block.options = options;
    blocks.push(block);
  }
  for (const b of base.blocks) if (!seen.has(blockId(b))) blocks.push(b);
  return { v: 1, blocks };
}

const ENTITY_ID = /^[0-9A-Za-z_-]{1,64}$/;
const CURRENCY = /^[A-Z]{3}$/;

function entityIds(raw: unknown): string[] | null {
  if (!Array.isArray(raw) || raw.length > FIGURE_ENTITY_IDS_MAX) return null;
  const ids = [...new Set(raw)];
  return ids.every(x => typeof x === 'string' && ENTITY_ID.test(x)) ? (ids as string[]) : null;
}

const keyList = (v: unknown): string[] | undefined =>
  v === undefined ? undefined : Array.isArray(v) && v.length <= 50 && v.every(isKey) ? (v as string[]) : undefined;

/** The shape of a group-by (its field and relation keys are checked against the schema when the chart is drawn). */
function parseGroupBy(raw: unknown): AggregateGroupBy | null {
  if (!isObj(raw)) return null;
  if (raw.kind === 'field') return isKey(raw.field) ? { kind: 'field', field: raw.field } : null;
  if (raw.kind === 'organizer') return { kind: 'organizer' };
  if (raw.kind === 'role') {
    const relations = keyList(raw.relations);
    return raw.relations !== undefined && !relations ? null : { kind: 'role', ...(relations ? { relations } : {}) };
  }
  if (raw.kind === 'link_end') {
    const relations = keyList(raw.relations);
    return raw.relations !== undefined && !relations ? null : { kind: 'link_end', ...(relations ? { relations } : {}) };
  }
  if (raw.kind !== 'year') return null;
  if (raw.of === 'field') return isKey(raw.field) ? { kind: 'year', of: 'field', field: raw.field } : null;
  if (raw.of !== 'link') return null;
  const relations = keyList(raw.relations);
  return raw.relations !== undefined && !relations ? null : { kind: 'year', of: 'link', ...(relations ? { relations } : {}) };
}

function parseMeasure(raw: unknown): AggregateMeasure | null {
  if (!isObj(raw)) return null;
  if (raw.kind === 'count') return { kind: 'count' };
  if (raw.kind === 'links') {
    const relations = keyList(raw.relations);
    return raw.relations !== undefined && !relations ? null : { kind: 'links', ...(relations ? { relations } : {}) };
  }
  if (raw.kind !== 'sum' || typeof raw.currency !== 'string' || !CURRENCY.test(raw.currency)) return null;
  const relations = keyList(raw.relations);
  if (raw.relations !== undefined && !relations) return null;
  return { kind: 'sum', currency: raw.currency, ...(relations ? { relations } : {}) };
}

/**
 * A figure's spec as the client saves it, with ids and keys in shape (the schema is checked when the figure is
 * drawn: `parseAggregateRequest` for a chart). Unknown properties are dropped; anything else → null.
 */
export function parseFigureSpec(raw: unknown): FigureSpec | null {
  if (!isObj(raw) || !(DOSSIER_FIGURE_KINDS as readonly unknown[]).includes(raw.kind)) return null;
  if (raw.kind === 'chart') {
    if (!isObj(raw.request) || !(FIGURE_CHART_TYPES as readonly unknown[]).includes(raw.chart)) return null;
    const query = parseReportQuery(raw.request.query);
    const groupBy = parseGroupBy(raw.request.groupBy);
    const splitBy = raw.request.splitBy === undefined || raw.request.splitBy === null ? null : parseGroupBy(raw.request.splitBy);
    const measure = parseMeasure(raw.request.measure);
    const within = raw.request.within === undefined || raw.request.within === null ? null : parseReportQuery(raw.request.within);
    if (!query.ok || !groupBy || !measure || (raw.request.splitBy && !splitBy) || (within && !within.ok)) return null;
    const rate = (AGGREGATE_RATES as readonly unknown[]).includes(raw.request.rate) ? (raw.request.rate as AggregateRate) : undefined;
    const request = {
      query: query.value,
      groupBy,
      splitBy,
      measure,
      ...(within?.ok ? { within: within.value } : {}),
      ...(rate ? { rate } : {}),
    };
    return { kind: 'chart', request, chart: raw.chart as FigureChartType };
  }
  const typed = parseTypedSpec(raw);
  if (typed !== undefined) return typed;
  const ids = entityIds(raw.entityIds);
  if (!ids) return null;
  if (raw.kind === 'votes') {
    if (raw.eventIds === undefined) return { kind: 'votes', entityIds: ids };
    const events = entityIds(raw.eventIds);
    return events ? { kind: 'votes', entityIds: ids, eventIds: events } : null;
  }
  if (raw.kind === 'graph') {
    if (raw.depth === undefined) return { kind: 'graph', entityIds: ids };
    const depth = Number(raw.depth);
    return Number.isInteger(depth) && depth >= 1 && depth <= 3 ? { kind: 'graph', entityIds: ids, depth } : null;
  }
  if (raw.kind === 'sankey')
    return typeof raw.currency === 'string' && CURRENCY.test(raw.currency)
      ? { kind: 'sankey', entityIds: ids, currency: raw.currency }
      : null;
  return { kind: raw.kind as 'timeline' | 'clusters' | 'centrality' | 'crosscheck', entityIds: ids };
}

const line = (v: unknown, max: number): string | null =>
  typeof v === 'string' && v.trim() ? v.replace(/\s+/g, ' ').trim().slice(0, max) : null;
const sourceOf = (v: unknown): string | null => line(v, FIGURE_SOURCE_CHARS);
const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null;
const slotOf = (v: unknown): number | null => (Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 12 ? (v as number) : null);

/**
 * The typed kinds (their data is in the spec): tiles, schematic, status timeline, before/after. Each is checked for
 * shape and limits; text is one line; anything else → null. Another kind → undefined (not typed).
 */
function parseTypedSpec(raw: Record<string, unknown>): FigureSpec | null | undefined {
  if (raw.kind === 'tiles') {
    if (!Array.isArray(raw.tiles) || !raw.tiles.length || raw.tiles.length > TILES_MAX) return null;
    const tiles: TileSpec[] = [];
    for (const t of raw.tiles) {
      if (!isObj(t)) return null;
      const value = line(t.value, TILE_VALUE_CHARS);
      const caption = line(t.caption, TILE_CAPTION_CHARS);
      if (!value || !caption) return null;
      tiles.push({ value, caption });
    }
    return { kind: 'tiles', tiles, source: sourceOf(raw.source) };
  }
  if (raw.kind === 'schematic') {
    if (!Array.isArray(raw.nodes) || !raw.nodes.length || raw.nodes.length > SCHEMATIC_MAX_NODES) return null;
    if (!Array.isArray(raw.links) || raw.links.length > SCHEMATIC_MAX_LINKS) return null;
    const nodes: SchematicNode[] = [];
    for (const n of raw.nodes) {
      if (!isObj(n) || typeof n.id !== 'string' || !/^[0-9A-Za-z_-]{1,64}$/.test(n.id) || nodes.some(x => x.id === n.id)) return null;
      const label = line(n.label, SCHEMATIC_LABEL_CHARS);
      const column = num(n.column);
      if (!label || column === null || !Number.isInteger(column) || column < 0 || column >= SCHEMATIC_MAX_COLUMNS) return null;
      nodes.push({ id: n.id, label, sublabel: line(n.sublabel, SCHEMATIC_LABEL_CHARS), column, slot: slotOf(n.slot) });
    }
    const ids = new Set(nodes.map(n => n.id));
    const links: SchematicLink[] = [];
    for (const l of raw.links) {
      if (
        !isObj(l) ||
        typeof l.from !== 'string' ||
        typeof l.to !== 'string' ||
        !ids.has(l.from) ||
        !ids.has(l.to) ||
        !isSchematicLinkKind(l.kind)
      )
        return null;
      links.push({ from: l.from, to: l.to, kind: l.kind, label: line(l.label, SCHEMATIC_LABEL_CHARS) });
    }
    return { kind: 'schematic', nodes, links, source: sourceOf(raw.source) };
  }
  if (raw.kind === 'status_timeline') {
    if (!Array.isArray(raw.events) || !raw.events.length || raw.events.length > STATUS_TIMELINE_MAX_EVENTS) return null;
    const events: StatusTimelineEvent[] = [];
    for (const e of raw.events) {
      if (!isObj(e) || !isReliabilityStatus(e.status)) return null;
      const date = line(e.date, 40);
      const label = line(e.label, STATUS_TIMELINE_LABEL_CHARS);
      if (!date || !label) return null;
      events.push({ date, label, status: e.status, slot: slotOf(e.slot) });
    }
    return { kind: 'status_timeline', events, source: sourceOf(raw.source) };
  }
  if (raw.kind === 'before_after') {
    if (!Array.isArray(raw.pairs) || !raw.pairs.length || raw.pairs.length > BEFORE_AFTER_MAX_PAIRS) return null;
    const pairs: BeforeAfterPair[] = [];
    for (const pr of raw.pairs) {
      if (!isObj(pr)) return null;
      const label = line(pr.label, SCHEMATIC_LABEL_CHARS);
      const before = num(pr.before);
      const after = num(pr.after);
      if (!label || before === null || after === null || before < 0 || after < 0) return null;
      pairs.push({ label, before, after });
    }
    const beforeLabel = line(raw.beforeLabel, 40);
    const afterLabel = line(raw.afterLabel, 40);
    if (!beforeLabel || !afterLabel) return null;
    const currency = typeof raw.currency === 'string' && CURRENCY.test(raw.currency) ? raw.currency : null;
    return { kind: 'before_after', pairs, beforeLabel, afterLabel, currency, source: sourceOf(raw.source) };
  }
  return undefined;
}

/** A figure caption as saved: one line of at most REPORT_LIMITS.captionChars. */
export const cleanCaption = (v: unknown): string =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, REPORT_LIMITS.captionChars) : '';

/** The brief a report was asked for: trimmed, at most REPORT_LIMITS.briefChars; empty → null. */
export function parseBrief(v: unknown): ParseResult<string | null> {
  if (v === undefined || v === null) return { ok: true, value: null };
  if (typeof v !== 'string') return { ok: false, error: 'The description must be text.' };
  const s = v.replace(/\r\n?/g, '\n').trim();
  if (s.length > REPORT_LIMITS.briefChars)
    return { ok: false, error: `Keep the description under ${REPORT_LIMITS.briefChars} characters.` };
  return { ok: true, value: s || null };
}
